import { useTranslation } from 'react-i18next';
import { Database, Globe, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import type { ToolKind } from '@/api/types';

interface Props {
  value: ToolKind;
  onChange: (kind: ToolKind) => void;
  locked: boolean;
}

export function Step1Kind({ value, onChange, locked }: Props): JSX.Element {
  const { t } = useTranslation();
  const options: Array<{ kind: ToolKind; label: string; desc: string; icon: typeof Database; isNew?: boolean }> = [
    {
      kind: 'elasticsearch',
      label: t('tools.wizard.step1.elasticsearch'),
      desc: t('tools.wizard.step1.elasticsearchDesc'),
      icon: Search,
    },
    {
      kind: 'bigquery',
      label: t('tools.wizard.step1.bigquery'),
      desc: t('tools.wizard.step1.bigqueryDesc'),
      icon: Database,
    },
    {
      kind: 'api',
      label: t('tools.wizard.step1.api'),
      desc: t('tools.wizard.step1.apiDesc'),
      icon: Globe,
      isNew: true,
    },
  ];
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {t('tools.wizard.step1.intro', { lock: locked ? t('tools.wizard.step1.lockedEdit') : t('tools.wizard.step1.lockedCreate') })}
      </p>
      <div className="grid gap-3 md:grid-cols-3">
        {options.map((opt) => {
          const Icon = opt.icon;
          const active = value === opt.kind;
          return (
            <button
              key={opt.kind}
              type="button"
              onClick={() => !locked && onChange(opt.kind)}
              disabled={locked}
              className={cn(
                'flex items-start gap-3 rounded-lg border p-4 text-left transition-colors',
                active
                  ? 'border-primary bg-primary/5'
                  : 'hover:border-primary/50 hover:bg-muted/30',
                locked && 'cursor-not-allowed opacity-60',
              )}
            >
              <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="font-medium">{opt.label}</span>
                  {opt.isNew && (
                    <Badge variant="default" className="text-[9px] px-1.5 py-0">
                      {t('common.new')}
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{opt.desc}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
